import { useEffect, useState, useCallback, useRef } from 'react'
import { supabase, checkConnection } from '../lib/supabase'
import type { User } from '@supabase/supabase-js'
import { debug } from '../lib/debug'
import { DEBUG, SITE_CONFIG } from '../lib/constants'

// Routes that require authentication
const AUTH_REQUIRED_ROUTES = [
  '/survey/create',
  '/survey/results',
  '/survey/manage',
  '/survey/insights',
  '/auth/update-password'
];

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(false) // Start as not loading
  const [isInitialized, setIsInitialized] = useState(false) // Track if auth is initialized
  const [isAdmin, setIsAdmin] = useState(false)
  const [isDatabaseOffline, setIsDatabaseOffline] = useState(SITE_CONFIG.FORCE_DATABASE_OFFLINE || false)
  const mountedRef = useRef(true)
  const adminCheckInProgressRef = useRef(false)
  const lastAdminCheckRef = useRef<{ userId: string; timestamp: number; result: boolean } | null>(null)
  const adminCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Check database connection status
  const checkDatabaseStatus = useCallback(async () => {
    if (SITE_CONFIG.FORCE_DATABASE_OFFLINE) {
      setIsDatabaseOffline(true)
      return false
    }
    
    if (!SITE_CONFIG.DATABASE_OFFLINE_DETECTION) {
      return true
    }
    
    const isOnline = await checkConnection()
    if (mountedRef.current) {
      setIsDatabaseOffline(!isOnline)
    }
    return isOnline
  }, [])

  const checkAdminStatus = useCallback(async (userId: string) => {
    // Skip if unmounted
    if (!mountedRef.current) return false
    
    // Skip if database is offline
    if (isDatabaseOffline) {
      debug(DEBUG.STATE, 'Database is offline, skipping admin check')
      return false
    }

    // Skip if already checking
    if (adminCheckInProgressRef.current) {
      debug(DEBUG.STATE, 'Admin check already in progress, skipping')
      return isAdmin
    }

    // Use cached result if recent (within last 5 seconds)
    const now = Date.now()
    if (lastAdminCheckRef.current && 
        lastAdminCheckRef.current.userId === userId && 
        now - lastAdminCheckRef.current.timestamp < 5000) {
      debug(DEBUG.STATE, 'Using cached admin status', lastAdminCheckRef.current)
      return lastAdminCheckRef.current.result
    }

    adminCheckInProgressRef.current = true
    
    try {
      debug(DEBUG.STATE, 'Fetching admin status from survey_managers table')
      const { data, error } = await supabase
        .from('survey_managers')
        .select('is_admin')
        .eq('user_id', userId)
        .single()

      if (error) {
        // Check if error is due to database being offline
        if (error.code === 'offline') {
          setIsDatabaseOffline(true)
          return false
        }
        
        debug(DEBUG.STATE, 'Admin check failed', { error })
        return false
      }

      const adminStatus = !!data?.is_admin
      debug(DEBUG.STATE, 'Admin status query result', { 
        data,
        userId,
        isAdmin: adminStatus
      })

      // Cache the result
      lastAdminCheckRef.current = {
        userId,
        timestamp: now,
        result: adminStatus
      }

      if (mountedRef.current) {
        debug(DEBUG.STATE, 'Setting admin status', {
          from: isAdmin,
          to: adminStatus,
          userId
        })
        setIsAdmin(adminStatus)
      }

      return adminStatus
    } catch (error) {
      debug(DEBUG.STATE, 'Admin check error', { error })
      return false
    } finally {
      adminCheckInProgressRef.current = false
    }
  }, [isAdmin, isDatabaseOffline])

  // Function to check if the current route requires authentication
  const isAuthRequiredForCurrentRoute = useCallback(() => {
    const currentPath = window.location.pathname;
    return AUTH_REQUIRED_ROUTES.some(route => currentPath.startsWith(route));
  }, []);

  // Initialize auth state - only when explicitly called
  const initializeAuth = useCallback(async () => {
    // Skip if already initialized or initializing
    if (isInitialized || isLoading) return;
    
    setIsLoading(true);
    debug(DEBUG.STATE, 'Initializing auth')
    
    // Check database connection first
    await checkDatabaseStatus()
    
    // If database is offline, skip authentication
    if (isDatabaseOffline) {
      debug(DEBUG.STATE, 'Database is offline, skipping auth initialization')
      setIsLoading(false)
      setIsInitialized(true)
      return
    }
    
    try {
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (!mountedRef.current) return
      
      // Check for offline error
      if (error && error.code === 'offline') {
        setIsDatabaseOffline(true)
        setIsLoading(false)
        setIsInitialized(true)
        return
      }

      if (session?.user) {
        debug(DEBUG.STATE, 'Setting user from session', {
          userId: session.user.id
        })
        setUser(session.user)
        await checkAdminStatus(session.user.id)
      } else {
        setUser(null)
        setIsAdmin(false)
      }
    } catch (error) {
      debug(DEBUG.STATE, 'Auth initialization error', { error })
    } finally {
      if (mountedRef.current) {
        setIsLoading(false)
        setIsInitialized(true)
      }
    }
  }, [checkAdminStatus, checkDatabaseStatus, isDatabaseOffline, isInitialized, isLoading]);

  // Only set up auth on mount if we're on a protected route
  useEffect(() => {
    debug(DEBUG.STATE, 'Auth hook mounted')
    mountedRef.current = true

    // Only initialize auth if we're on a route that requires it
    if (isAuthRequiredForCurrentRoute()) {
      debug(DEBUG.STATE, 'On protected route, initializing auth')
      initializeAuth()
    } else {
      debug(DEBUG.STATE, 'On public route, skipping auth initialization')
    }

    return () => {
      debug(DEBUG.STATE, 'Auth hook cleanup')
      mountedRef.current = false
      if (adminCheckTimeoutRef.current) {
        clearTimeout(adminCheckTimeoutRef.current)
      }
    }
  }, [initializeAuth, isAuthRequiredForCurrentRoute])

  // Listen for auth changes - only if initialized
  useEffect(() => {
    // Skip if database is offline or not initialized
    if (isDatabaseOffline || !isInitialized) {
      return () => {}
    }
    
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mountedRef.current) return

      debug(DEBUG.STATE, 'Auth state changed', {
        event,
        userId: session?.user?.id
      })

      if (session?.user) {
        debug(DEBUG.STATE, 'Setting user from auth change', {
          userId: session.user.id
        })
        setUser(session.user)

        // Clear any existing timeout
        if (adminCheckTimeoutRef.current) {
          clearTimeout(adminCheckTimeoutRef.current)
        }

        // Add a small delay before checking admin status
        adminCheckTimeoutRef.current = setTimeout(async () => {
          await checkAdminStatus(session.user.id)
          if (mountedRef.current) {
            setIsLoading(false)
          }
        }, 100)
      } else {
        setUser(null)
        setIsAdmin(false)
        setIsLoading(false)
      }
    })

    return () => {
      subscription.unsubscribe()
      if (adminCheckTimeoutRef.current) {
        clearTimeout(adminCheckTimeoutRef.current)
      }
    }
  }, [checkAdminStatus, isDatabaseOffline, isInitialized])

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin,
    isDatabaseOffline,
    isInitialized,
    initializeAuth, // Expose this so we can manually initialize auth when needed
    shouldInitializeAuth: isAuthRequiredForCurrentRoute, // Expose function to check if auth is needed
  }
}